import { QueryCache, QueryClient } from '@tanstack/react-query';
import { APIServiceType } from '@/api/types';
import { reportQueryApiError } from './reportQueryApiError';
import { QUERY_PERSIST_MAX_AGE_MS } from './queryPersistConstants';

const isApiServiceType = (service: unknown): service is APIServiceType =>
  Object.values(APIServiceType).includes(service as APIServiceType);

/**
 * Default retry: exponential backoff (replaces legacy `retryWrapper` 2 retries + delay).
 * Per-query overrides (e.g. `retry: false` for non-critical fetches) still apply.
 */
export const createQueryClient = () =>
  new QueryClient({
    queryCache: new QueryCache({
      onError: (error, query) => {
        const service = query.meta?.apiService;

        if (isApiServiceType(service)) {
          reportQueryApiError(error, service);
        }
      },
    }),

    defaultOptions: {
      queries: {
        staleTime: 0,
        /** Must be ≥ PersistQueryClient `maxAge` or restored cache can be GC’d early. */
        gcTime: QUERY_PERSIST_MAX_AGE_MS,
        retry: 3,
        refetchOnWindowFocus: false,
        retryDelay: (attemptIndex) =>
          Math.min(500 * (attemptIndex + 1), 30_000),
      },
      mutations: {
        retry: 1,
      },
    },
  });
